/**
 * Azure AD B2C tenant configuration for MSAL authentication
 */
import { PublicClientApplication, Configuration } from '@azure/msal-browser';

// Environment variables for Azure AD B2C configuration
const {
  B2C_TENANT_NAME: tenantName = '',
  B2C_TENANT_ID: tenantId = '',
  B2C_CLIENT_ID: clientId = '',
  SSO_URL: ssoURL = '',
} = process.env;

const signInOrSignUpPolicy = 'B2C_1A_SEAMLESSMIGRATION_SIGNIN'; // policy for sign-in/sign-up
const changePasswordPolicy = 'B2C_1A_PASSWORDCHANGE'; // policy for password change
const protocol = 'https://'; // Protocol for the authority URL

// Azure AD B2C login domain
const B2C_LOGIN_DOMAIN = 'b2clogin.com'; // Constant for .b2clogin.com

// Use tenantName if tenantId is not defined
let resolvedTenant = tenantId || tenantName;

// Append ".onmicrosoft.com" if resolvedTenant is derived from tenantName
if (!tenantId && tenantName) {
  resolvedTenant += '.onmicrosoft.com';
}

// Determine the base authority URL and known authorities based on ssoURL
// If ssoURL is defined, use it, otherwise fallback to default B2C URL
const BASE_AUTHORITY_URL = ssoURL
  ? `${protocol}${ssoURL}/${resolvedTenant}` // Use custom SSO URL
  : `${protocol}${tenantName}.${B2C_LOGIN_DOMAIN}/${resolvedTenant}`; // Default B2C URL

// Known authorities list: either custom SSO URL or default B2C login URL
const KNOWN_AUTHORITIES = ssoURL
  ? [ssoURL] // Use the custom SSO URL as known authority
  : [`${tenantName}.${B2C_LOGIN_DOMAIN}`]; // Default B2C login known authority

// Default scopes required for authentication
export const SCOPES = ['openid', 'profile', 'offline_access'];

// MSAL configuration object with client ID, authority URL, and other settings
const msalConfig: Configuration = {
  auth: {
    clientId, // Azure AD B2C app client ID
    authority: `${BASE_AUTHORITY_URL}/${signInOrSignUpPolicy}`, // Authority URL with sign-in policy
    knownAuthorities: KNOWN_AUTHORITIES, // Known authorities to trust
    redirectUri: '/', // Redirect URI after successful authentication
    postLogoutRedirectUri: '/', // URI to redirect to after logout
  },
  cache: {
    cacheLocation: 'localStorage', // Store cache in localStorage for persistence
    storeAuthStateInCookie: false, // Avoid storing auth state in cookies
  },
};

/**
 * Utility function to generate MSAL request configurations for different flows
 * @param language User's preferred language (e.g., 'en', 'fr')
 * @param authority Optional: Specific authority (overrides default authority)
 * @param prompt Optional: Prompt behavior (e.g., 'login', 'consent')
 * @returns MSAL request object
 */
const createRequest = (
  language: string,
  authority?: string,
  prompt?: string,
) => ({
  scopes: SCOPES,
  extraQueryParameters: { ui_locales: language },
  ...(authority && { authority }),
  ...(prompt && { prompt }),
});

// Predefined MSAL request configurations for common flows
const loginRequest = (language: string) =>
  createRequest(language, undefined, 'login');

const changePasswordRequest = (language: string) =>
  createRequest(language, `${BASE_AUTHORITY_URL}/${changePasswordPolicy}`);

const logOutRequest = (language: string) => createRequest(language);

// Create and export MSAL instance
export const msalInstance = new PublicClientApplication(msalConfig);

// Export configurations and request functions
export { msalConfig, loginRequest, changePasswordRequest, logOutRequest };
